import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Euro, Layers, Rocket, ShieldCheck, Wallet } from "lucide-react";
import api from "@/lib/api";

const fmt = (n) => `${Number(n || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

function KindBadge({ kind }) {
  const map = {
    kickstart_overdue: { txt: "Arranque em atraso", cls: "bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30" },
    kickstart_pending: { txt: "Arranque pendente", cls: "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30" },
    remainder_overdue: { txt: "Remanescente em atraso", cls: "bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30" },
    remainder_pending: { txt: "Remanescente pendente", cls: "bg-muted text-muted-foreground border-border" },
  };
  const m = map[kind] || { txt: kind, cls: "bg-muted text-muted-foreground border-border" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.12em] font-semibold border ${m.cls}`}>
      {m.txt}
    </span>
  );
}

const formatDeadline = (row) => {
  if (!row.deadline_iso) return "—";
  const d = new Date(row.deadline_iso);
  const s = d.toLocaleDateString("pt-PT", { day: "2-digit", month: "short" });
  if (row.overdue_hours > 0) return `há ${row.overdue_hours}h`;
  const diffH = Math.round((d - new Date()) / 3600000);
  if (diffH <= 48 && diffH >= 0) return `em ${diffH}h`;
  return s;
};

export default function DepositSummaryPanel({ fetcher }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // 25/02/2026 — Custom `fetcher` prop permite reutilizar o
        // componente no portal público DAF (`/daf-dashboard`) onde a
        // autenticação é por magic-link token em vez de sessão.
        const payload = fetcher ? await fetcher() : (await api.get("/dashboard/deposit-summary")).data;
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setErr(true);
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [fetcher]);

  if (err || !data) return null;

  const totalOverdue = (data.overdue_kickstart_count || 0) + (data.overdue_remainder_count || 0);
  const totalOverdueAmt = (data.overdue_kickstart_amount || 0) + (data.overdue_remainder_amount || 0);
  const totalOpenAmt = (data.kickstart_pending_amount || 0) + (data.remainder_pending_amount || 0);

  // 25/02/2026 — 3 valores headline pedidos pelo utilizador.
  const headline = [
    {
      key: "total",
      label: "Total da Caução",
      hint: "Somatório do plano definido",
      icon: Layers,
      planned: data.caucao_total_planned,
      paid: data.caucao_total_paid,
      open: data.caucao_total_open,
      accent: "text-foreground",
    },
    {
      key: "arranque",
      label: "Arranque",
      hint: "Kickstart p/ arranque de recrutamento",
      icon: Rocket,
      planned: data.arranque_planned,
      paid: data.arranque_paid,
      open: data.arranque_open,
      accent: "text-[hsl(var(--info))]",
    },
    {
      key: "remanescente",
      label: "Remanescente",
      hint: "Total − Arranque, liquidar até início do serviço",
      icon: Wallet,
      planned: data.remanescente_planned,
      paid: data.remanescente_paid,
      open: data.remanescente_open,
      accent: "text-[hsl(var(--warning))]",
    },
  ];

  return (
    <div className="bg-card border border-border rounded-2xl p-6 lg:p-8 mb-8" data-testid="deposit-summary-panel">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
            <Wallet className="w-4 h-4 text-foreground" />
          </div>
          <div>
            <h3 className="font-ui text-lg text-foreground">Cauções — Painel DAF</h3>
            <div className="text-xs text-muted-foreground">Pagamentos por confirmar antes do arranque do serviço</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-muted-foreground" data-testid="deposit-plan-defined">
            {data.plan_defined_count} plano{data.plan_defined_count === 1 ? "" : "s"} ativo{data.plan_defined_count === 1 ? "" : "s"}
          </span>
          <span className="text-[hsl(var(--success))] font-semibold" data-testid="deposit-fully-paid">
            {data.fully_paid_count} pago{data.fully_paid_count === 1 ? "" : "s"}
          </span>
        </div>
      </div>

      {/* Headline: Total da Caução · Arranque · Remanescente (25/02/2026) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {headline.map((c) => {
          const Icon = c.icon;
          const paidPct = c.planned > 0 ? Math.min(100, Math.round((c.paid / c.planned) * 100)) : 0;
          return (
            <div
              key={c.key}
              className="bg-muted/30 border border-border rounded-xl p-5"
              data-testid={`deposit-headline-${c.key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">{c.label}</div>
                <Icon className={`w-4 h-4 ${c.accent}`} strokeWidth={1.5} />
              </div>
              <div
                className={`font-ui text-3xl tracking-tight tabular-nums ${c.accent}`}
                data-testid={`deposit-headline-${c.key}-planned`}
              >
                {fmt(c.planned)}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">{c.hint}</div>
              <div className="mt-3 flex items-center gap-2 text-[11px] tabular-nums">
                <span className="text-[hsl(var(--success))]" data-testid={`deposit-headline-${c.key}-paid`}>
                  {fmt(c.paid)} pago
                </span>
                <span className="text-muted-foreground/50">·</span>
                <span
                  className={c.open > 0 ? "text-foreground font-semibold" : "text-muted-foreground"}
                  data-testid={`deposit-headline-${c.key}-open`}
                >
                  {fmt(c.open)} em aberto
                </span>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-[hsl(var(--success))]/60"
                  style={{ width: `${paidPct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Row operacional: pendências + atrasos (contexto do dia-a-dia) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-muted/40 border border-border rounded-xl p-4" data-testid="stat-kickstart-pending">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Arranque pendente</div>
            <Euro className="w-3.5 h-3.5 text-muted-foreground/70" strokeWidth={1.5} />
          </div>
          <div className="font-ui text-2xl tracking-tight text-foreground">{data.kickstart_pending_count}</div>
          <div className="text-xs text-muted-foreground mt-1 tabular-nums">{fmt(data.kickstart_pending_amount)}</div>
        </div>
        <div className="bg-muted/40 border border-border rounded-xl p-4" data-testid="stat-remainder-pending">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Remanescente pendente</div>
            <Euro className="w-3.5 h-3.5 text-muted-foreground/70" strokeWidth={1.5} />
          </div>
          <div className="font-ui text-2xl tracking-tight text-foreground">{data.remainder_pending_count}</div>
          <div className="text-xs text-muted-foreground mt-1 tabular-nums">{fmt(data.remainder_pending_amount)}</div>
        </div>
        <div className={`rounded-xl p-4 border ${totalOverdue > 0 ? "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/30" : "bg-muted/40 border-border"}`} data-testid="stat-overdue">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Em atraso</div>
            <AlertTriangle className={`w-3.5 h-3.5 ${totalOverdue > 0 ? "text-[hsl(var(--danger))]" : "text-muted-foreground/70"}`} strokeWidth={1.5} />
          </div>
          <div className={`font-ui text-2xl tracking-tight ${totalOverdue > 0 ? "text-[hsl(var(--danger))]" : "text-foreground"}`}>{totalOverdue}</div>
          <div className={`text-xs mt-1 tabular-nums ${totalOverdue > 0 ? "text-[hsl(var(--danger))]" : "text-muted-foreground"}`}>{fmt(totalOverdueAmt)}</div>
        </div>
        <div className="bg-[hsl(var(--success-bg))] border border-[hsl(var(--success))]/30 rounded-xl p-4" data-testid="stat-open-total">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--success))] font-semibold">Total em aberto</div>
            <ShieldCheck className="w-3.5 h-3.5 text-[hsl(var(--success))]" strokeWidth={1.5} />
          </div>
          <div className="font-ui text-2xl tracking-tight text-[hsl(var(--success))] tabular-nums">{fmt(totalOpenAmt)}</div>
          <div className="text-xs text-[hsl(var(--success))]/80 mt-1">a confirmar pelo DAF</div>
        </div>
      </div>

      {/* 25/02/2026 (v3) — Vista unificada sem separadores (pedido do
          utilizador: "em cada linha deveria mostrar o que já foi pago e
          não ter separadores"). Uma única tabela ordenada por urgência,
          com colunas explícitas de Total plano / Já pago / Em aberto
          por lead. Fonte: `pending_leads_all` (até 200 rows). */}
      {(() => {
        const rows = data.pending_leads_all
          || [...(data.pending_leads_kickstart || []), ...(data.pending_leads_remainder || [])];
        const totalPending = (data.kickstart_pending_count || 0) + (data.remainder_pending_count || 0);
        if (rows.length === 0) {
          return (
            <div className="text-sm text-muted-foreground py-4 text-center" data-testid="deposit-empty">
              Sem cauções pendentes de confirmação.
            </div>
          );
        }

        // 25/02/2026 — Aging buckets para arranques (pedido: "Group
        // pending arranques by aging so overdue clusters stand out").
        // Estilos por severity:
        const bucketStyles = {
          info:    "bg-muted/50 border-border text-muted-foreground",
          warning: "bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/30 text-[hsl(var(--warning))]",
          danger:  "bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/30 text-[hsl(var(--danger))]",
          critical:"bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/60 text-[hsl(var(--danger))] ring-1 ring-[hsl(var(--danger))]/30",
        };
        const bucketRowTint = {
          info: "",
          warning: "bg-[hsl(var(--warning-bg))]/30",
          danger: "bg-[hsl(var(--danger-bg))]/30",
          critical: "bg-[hsl(var(--danger-bg))]/50",
        };
        const bucketBySeverity = (key) => (data.arranque_aging || []).find(b => b.key === key)?.severity || "info";

        return (
          <div data-testid="deposit-pending-list">
            {(data.arranque_aging || []).length > 0 && (
              <div className="mb-4" data-testid="deposit-aging-band">
                <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold mb-2">
                  Aging dos arranques
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {data.arranque_aging.map((b) => (
                    <div
                      key={b.key}
                      data-testid={`aging-bucket-${b.key}`}
                      className={`rounded-lg border p-3 transition-all ${bucketStyles[b.severity] || bucketStyles.info} ${b.count === 0 ? "opacity-40" : ""}`}
                    >
                      <div className="text-[9px] uppercase tracking-[0.18em] font-bold opacity-80">{b.label}</div>
                      <div className="font-ui text-2xl tracking-tight tabular-nums mt-1" data-testid={`aging-bucket-${b.key}-count`}>{b.count}</div>
                      <div className="text-[10px] tabular-nums opacity-80 mt-0.5">{fmt(b.amount)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold" data-testid="deposit-pending-caption">
                Cauções pendentes · {rows.length}{totalPending > rows.length ? ` de ${totalPending}` : ""}
              </div>
              <div className="text-[10px] text-muted-foreground/80">
                Ordenadas por urgência (arranques em atraso primeiro)
              </div>
            </div>
            <div className="overflow-x-auto max-h-[560px] overflow-y-auto rounded-lg border border-border/60">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card z-10">
                  <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold border-b border-border">
                    <th className="text-left py-2.5 px-3">Cliente</th>
                    <th className="text-left py-2.5 px-3">Estado</th>
                    <th className="text-right py-2.5 px-3">Total da caução</th>
                    <th className="text-right py-2.5 px-3">Arranque</th>
                    <th className="text-right py-2.5 px-3">Já pago</th>
                    <th className="text-right py-2.5 px-3">Em aberto</th>
                    <th className="text-right py-2.5 px-3">Prazo</th>
                    <th className="py-2.5 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isKick = (row.deadline_kind || "").startsWith("kickstart");
                    // 25/02/2026 (v4) — Colunas fixas por linha:
                    //   • Total da caução: valor do plano completo
                    //   • Arranque: valor pedido para arranque (kickstart)
                    //   • Já pago: total pago pelo cliente até agora
                    //   • Em aberto: falta pagar (nesta fase)
                    const totalCaucao = row.total_target || 0;
                    const arranqueTarget = row.kickstart_target || 0;
                    const totalPaid = row.total_paid || 0;
                    // 25/02/2026 — Tinta suave por aging bucket para
                    // arranques (destaca clusters overdue visualmente).
                    const rowSev = row.aging_bucket ? bucketBySeverity(row.aging_bucket) : "info";
                    const rowTint = isKick ? (bucketRowTint[rowSev] || "") : "";
                    return (
                      <tr key={row.id} className={`border-b border-border/60 last:border-b-0 hover:bg-muted/30 ${rowTint}`} data-testid={`deposit-row-${row.id}`}>
                        <td className="py-2 px-3">
                          <div className="text-sm text-foreground truncate max-w-[220px]" data-testid={`deposit-row-${row.id}-name`}>{row.name}</div>
                          <div className="flex items-center gap-2 flex-wrap mt-0.5">
                            {row.accepted_at && (() => {
                              const days = Math.max(0, Math.floor((Date.now() - new Date(row.accepted_at).getTime()) / 86400000));
                              // 25/02/2026 — Sinal de "stale": verde <3d,
                              // amber 3-7d, vermelho >7d. Ajuda o DAF a
                              // ver de relance quão parado está cada deal.
                              const staleTone = days > 7
                                ? "text-[hsl(var(--danger))]"
                                : days >= 3
                                ? "text-[hsl(var(--warning))]"
                                : "text-[hsl(var(--success))]";
                              const daysLabel = days === 0 ? "hoje" : days === 1 ? "há 1 dia" : `há ${days} dias`;
                              return (
                                <div className={`text-[10px] font-semibold flex items-center gap-1 ${staleTone}`} data-testid={`deposit-row-${row.id}-adjudicado`}>
                                  <span>Adjudicado: {new Date(row.accepted_at).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" })}</span>
                                  <span className="opacity-70" data-testid={`deposit-row-${row.id}-days-since`}>· {daysLabel}</span>
                                </div>
                              );
                            })()}
                            {row.service_start_date && (
                              <div className="text-[10px] text-muted-foreground">
                                Início: {new Date(row.service_start_date).toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" })}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3"><KindBadge kind={row.deadline_kind} /></td>
                        <td className="py-2 px-3 text-right tabular-nums text-foreground" data-testid={`deposit-row-${row.id}-total-caucao`}>
                          {fmt(totalCaucao)}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums text-muted-foreground" data-testid={`deposit-row-${row.id}-arranque`}>
                          {fmt(arranqueTarget)}
                        </td>
                        <td className={`py-2 px-3 text-right tabular-nums ${totalPaid > 0 ? "text-[hsl(var(--success))]" : "text-muted-foreground/60"}`} data-testid={`deposit-row-${row.id}-paid`}>
                          {fmt(totalPaid)}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums font-semibold text-foreground" data-testid={`deposit-row-${row.id}-open`}>
                          {fmt(row.open_amount)}
                        </td>
                        <td className={`py-2 px-3 text-right tabular-nums ${row.overdue_hours > 0 ? "text-[hsl(var(--danger))] font-semibold" : "text-muted-foreground"}`}>
                          {formatDeadline(row)}
                        </td>
                        <td className="py-2 px-3 text-right">
                          <Link
                            to={`/leads/${row.id}`}
                            data-testid={`deposit-open-${row.id}`}
                            className="inline-flex items-center gap-1 text-[11px] text-foreground hover:underline font-semibold"
                          >
                            Abrir →
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
